import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Nav, NavItem, NavLink, TabContent, TabPane,
    Table, Spinner, Alert, Badge,
    Modal, ModalHeader, ModalBody, ModalFooter, Button,
} from "reactstrap";
import classnames from "classnames";
import { getAuthUser } from "../../hooks/useAuthToken";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── interfaces ── */
interface CompanyUser {
    id: number;
    login: string;
    fullname?: string;
    firstname?: string;
    lastname?: string;
    email?: string;
    office_phone?: string;
    user_mobile?: string;
    job?: string;
    is_active?: boolean;
    statut?: number;
    datelastlogin?: string;
}

interface DefaultContact {
    assoc_id: number;
    type_code: string;
    type_label: string;
    contact: {
        id?: number;
        fullname?: string;
        email?: string;
        phone?: string;
        phone_mobile?: string;
        poste?: string;
    };
}

interface CompanyUsersData {
    users: CompanyUser[];
    users_count: number;
    default_contacts: DefaultContact[];
    default_contacts_count: number;
}

interface CompanyInfo {
    id?: string | number;
    name?: string;
    entity?: string | number;
    idprof2?: string;
    code_client?: string;
    email?: string;
    phone?: string;
    office_phone?: string;
    phone_mobile?: string;
    user_mobile?: string;
    address?: string;
    town?: string;
    zip?: string;
    country?: string;
    firstname?: string;
    lastname?: string;
    login?: string;
}

interface UserInfo {
    firstname?: string;
    lastname?: string;
    email?: string;
    phone_perso?: string;
    phone_mobile?: string;
    address?: string;
}

/* ── composant ── */
const UserProfile = () => {
    const [activeTab, setActiveTab]         = useState("1");
    const [companyInfo, setCompanyInfo]     = useState<CompanyInfo | null>(null);
    const [userInfo, setUserInfo]           = useState<UserInfo | null>(null);
    const [companyUsers, setCompanyUsers]   = useState<CompanyUsersData | null>(null);
    const [loading, setLoading]             = useState(true);
    const [error, setError]                 = useState<string | null>(null);

    const [modalAdd, setModalAdd]   = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError]   = useState<string | null>(null);
    const [addSuccess, setAddSuccess] = useState<string | null>(null);
    const [addForm, setAddForm]     = useState({
        civility: "", firstname: "", lastname: "",
        poste: "", email: "", phone: "", phone_mobile: "",
    });

    const [deactivatingId, setDeactivatingId] = useState<number | null>(null);
    const [deactError, setDeactError] = useState<string | null>(null);

    const toggleTab = (tab: string) => { if (activeTab !== tab) setActiveTab(tab); };

    const openAddModal = () => {
        setAddForm({ civility: "", firstname: "", lastname: "", poste: "", email: "", phone: "", phone_mobile: "" });
        setAddError(null);
        setAddSuccess(null);
        setModalAdd(true);
    };

    const deactivateUser = async (usr: CompanyUser) => {
        if (!window.confirm(`Désactiver l'utilisateur « ${usr.fullname || usr.login} » ?`)) return;
        setDeactivatingId(usr.id);
        setDeactError(null);
        try {
            await axios.post("/apiV2/", { action: "deactivateuser", id: usr.id }, apiV2PostConfig());
            // Recharger la liste
            const usersRes: any = await axios.post("/apiV2/", { action: "companyusers" }, apiV2PostConfig());
            const usersRaw = usersRes?.data ?? usersRes;
            if (usersRaw?.users) setCompanyUsers(usersRaw as CompanyUsersData);
        } catch (e: any) {
            setDeactError(typeof e === "string" ? e : e?.message || "Erreur lors de la désactivation.");
        } finally {
            setDeactivatingId(null);
        }
    };

    const submitAddContact = async () => {
        if (!addForm.firstname && !addForm.lastname) {
            setAddError("Le prénom ou le nom est requis.");
            return;
        }
        setAddLoading(true);
        setAddError(null);
        try {
            const result: any = await axios.post("/apiV2/", { action: "addcontact", ...addForm }, apiV2PostConfig());
            const resData = result?.data ?? result;
            const login = resData?.data?.login || resData?.login || "";
            setModalAdd(false);
            setAddSuccess(`Utilisateur ${login ? login + " " : ""}créé avec succès.`);
            // Recharger la liste des utilisateurs
            const usersRes: any = await axios.post("/apiV2/", { action: "companyusers" }, apiV2PostConfig());
            const usersRaw = usersRes?.data ?? usersRes;
            if (usersRaw?.users) setCompanyUsers(usersRaw as CompanyUsersData);
        } catch (e: any) {
            setAddError(typeof e === "string" ? e : e?.message || "Erreur lors de la création du contact.");
        } finally {
            setAddLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const authUser = getAuthUser();
                if (!authUser?.token) {
                    setError("Token d'authentification introuvable.");
                    return;
                }
                const cfg = apiV2PostConfig();

                const companyRes: any = await axios.post("/apiV2/", { action: "company" }, cfg);
                const raw = companyRes?.data ?? companyRes;
                if (raw && typeof raw === "object" && !raw.message) setCompanyInfo(raw);

                let userRes: any = null;
                try { userRes = await axios.post("/apiV2/", { action: "getUser" }, cfg); } catch {}
                if (!userRes || userRes?.message?.includes("Error")) {
                    try { userRes = await axios.post("/apiV2/", { action: "me" }, cfg); } catch {}
                }
                const uRaw = userRes?.data ?? userRes;
                if (uRaw && typeof uRaw === "object" && !uRaw.message?.includes("Error")) {
                    setUserInfo(uRaw);
                }

                try {
                    const usersRes: any = await axios.post("/apiV2/", { action: "companyusers" }, cfg);
                    const usersRaw = usersRes?.data ?? usersRes;
                    if (usersRaw && usersRaw.users) setCompanyUsers(usersRaw as CompanyUsersData);
                } catch {}
            } catch (err) {
                console.error(err);
                setError("Erreur lors de la récupération du profil.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    /* ── état chargement ── */
    if (loading) {
        return (
            <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="text-center">
                    <Spinner style={{ width: "3rem", height: "3rem", color: "#0066A1" }} />
                    <p className="text-muted mt-3 fw-medium">Chargement de votre profil…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Alert color="danger" className="mt-4">
                        <i className="ri-error-warning-line me-2"></i>{error}
                    </Alert>
                </Container>
            </div>
        );
    }

    /* ── données consolidées ── */
    const local = getAuthUser();
    const u = {
        firstname:    userInfo?.firstname    || local?.firstname    || companyInfo?.firstname || "",
        lastname:     userInfo?.lastname     || local?.lastname     || companyInfo?.lastname  || companyInfo?.name || "",
        email:        userInfo?.email        || local?.email        || companyInfo?.email     || local?.login || "",
        login:        local?.login           || companyInfo?.login  || "",
        phone:        userInfo?.phone_perso  || companyInfo?.phone  || companyInfo?.office_phone || "",
        mobile:       userInfo?.phone_mobile || companyInfo?.phone_mobile || companyInfo?.user_mobile || "",
        address:      userInfo?.address      || companyInfo?.address || "",
        town:         companyInfo?.town      || "",
        zip:          companyInfo?.zip       || "",
    };

    const fullName = [u.firstname, u.lastname].filter(Boolean).join(" ") || "Utilisateur";
    const initials = [u.firstname?.[0], u.lastname?.[0]].filter(Boolean).join("").toUpperCase() || "U";

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>

                    {/* ── Bandeau de fond IPTrend ── */}
                    <div className="profile-foreground position-relative mx-n4 mt-n4">
                        <div className="profile-wid-bg">
                            <div style={{
                                background: "linear-gradient(135deg, #0066A1 0%, #00A389 50%, #7AC943 100%)",
                                height: "100%", width: "100%", minHeight: 200,
                                opacity: 0.9,
                            }} />
                        </div>
                    </div>

                    {/* ── En-tête profil ── */}
                    <div className="pt-4 mb-4 mb-lg-3 pb-lg-4 profile-wrapper">
                        <Row className="g-4">
                            {/* Avatar initiales */}
                            <div className="col-auto">
                                <div className="avatar-lg">
                                    <span className="avatar-title rounded-circle fs-2 fw-bold text-white"
                                        style={{ background: "rgba(255,255,255,0.25)", border: "3px solid rgba(255,255,255,0.6)" }}>
                                        {initials}
                                    </span>
                                </div>
                            </div>

                            {/* Nom + rôle + société */}
                            <Col>
                                <div className="p-2">
                                    <h3 className="text-white mb-1">{fullName}</h3>
                                    <p className="text-white text-opacity-75 mb-1">{u.login}</p>
                                    <div className="hstack text-white-50 gap-1 flex-wrap">
                                        {(u.town || u.zip) && (
                                            <div className="me-2">
                                                <i className="ri-map-pin-user-line me-1 text-white text-opacity-75 fs-16 align-middle"></i>
                                                {[u.zip, u.town].filter(Boolean).join(" ")}
                                            </div>
                                        )}
                                        {companyInfo?.name && (
                                            <div>
                                                <i className="ri-building-line me-1 text-white text-opacity-75 fs-16 align-middle"></i>
                                                {companyInfo.name}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>

                            {/* Stats */}
                            <Col xs={12} className="col-lg-auto order-last order-lg-0">
                                <div className="p-2 text-center text-white-50">
                                    <h4 className="text-white mb-1 text-nowrap">
                                        {companyInfo?.code_client || "—"}
                                    </h4>
                                    <p className="fs-14 mb-0">Code client</p>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* ── Nav tabs ── */}
                    <Row>
                        <Col lg={12}>
                            <div>
                                <div className="d-flex profile-wrapper">
                                    <Nav pills className="animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1" role="tablist">
                                        <NavItem>
                                            <NavLink
                                                className={classnames("fs-14", { active: activeTab === "1" })}
                                                onClick={() => toggleTab("1")}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <i className="ri-airplay-fill d-inline-block d-md-none"></i>
                                                <span className="d-none d-md-inline-block">Vue d'ensemble</span>
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                className={classnames("fs-14", { active: activeTab === "2" })}
                                                onClick={() => toggleTab("2")}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <i className="ri-building-line d-inline-block d-md-none"></i>
                                                <span className="d-none d-md-inline-block">Entreprise</span>
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                className={classnames("fs-14", { active: activeTab === "3" })}
                                                onClick={() => toggleTab("3")}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <i className="ri-team-line d-inline-block d-md-none"></i>
                                                <span className="d-none d-md-inline-block">Utilisateurs</span>
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                </div>

                                {/* ══ TabContent ══ */}
                                <TabContent activeTab={activeTab} className="pt-4">

                                    {/* ── Tab 1 : Vue d'ensemble ── */}
                                    <TabPane tabId="1">
                                        <Row>
                                            {/* Colonne gauche */}
                                            <Col xxl={3}>

                                                {/* Info rapide */}
                                                <Card>
                                                    <CardBody>
                                                        <h5 className="card-title mb-3">Informations</h5>
                                                        <div className="table-responsive">
                                                            <Table className="table-borderless mb-0">
                                                                <tbody>
                                                                    <tr>
                                                                        <th className="ps-0" scope="row">Nom :</th>
                                                                        <td className="text-muted">{fullName || "—"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="ps-0" scope="row">Login :</th>
                                                                        <td className="text-muted">{u.login || "—"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="ps-0" scope="row">E-mail :</th>
                                                                        <td className="text-muted">{u.email || "—"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="ps-0" scope="row">Tél. :</th>
                                                                        <td className="text-muted">{u.phone || "—"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="ps-0" scope="row">Mobile :</th>
                                                                        <td className="text-muted">{u.mobile || "—"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="ps-0" scope="row">Ville :</th>
                                                                        <td className="text-muted">{[u.zip, u.town].filter(Boolean).join(" ") || "—"}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </CardBody>
                                                </Card>

                                                {/* Entreprise synthèse */}
                                                {companyInfo && (
                                                    <Card>
                                                        <CardBody>
                                                            <h5 className="card-title mb-3">Entreprise</h5>
                                                            <div className="table-responsive">
                                                                <Table className="table-borderless mb-0">
                                                                    <tbody>
                                                                        <tr>
                                                                            <th className="ps-0" scope="row">Raison sociale :</th>
                                                                            <td className="text-muted">{companyInfo.name || "—"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <th className="ps-0" scope="row">SIRET :</th>
                                                                            <td className="text-muted">{companyInfo.idprof2 || "—"}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <th className="ps-0" scope="row">Code client :</th>
                                                                            <td className="text-muted">
                                                                                <Badge color="primary" className="fs-12">
                                                                                    {companyInfo.code_client || "—"}
                                                                                </Badge>
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </CardBody>
                                                    </Card>
                                                )}

                                                {/* Accès rapides */}
                                                <Card>
                                                    <CardBody>
                                                        <h5 className="card-title mb-4">Accès rapides</h5>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            <Link to="/services" className="avatar-xs d-block">
                                                                <span className="avatar-title rounded-circle fs-16 bg-primary">
                                                                    <i className="ri-file-list-3-line"></i>
                                                                </span>
                                                            </Link>
                                                            <Link to="/mesfactures" className="avatar-xs d-block">
                                                                <span className="avatar-title rounded-circle fs-16 bg-success">
                                                                    <i className="ri-bill-line"></i>
                                                                </span>
                                                            </Link>
                                                            <Link to="/dashboard" className="avatar-xs d-block">
                                                                <span className="avatar-title rounded-circle fs-16 bg-info">
                                                                    <i className="ri-dashboard-3-line"></i>
                                                                </span>
                                                            </Link>
                                                        </div>
                                                    </CardBody>
                                                </Card>

                                            </Col>

                                            {/* Colonne droite */}
                                            <Col xxl={9}>

                                                {/* Carte de présentation */}
                                                <Card>
                                                    <CardBody>
                                                        <h5 className="card-title mb-3">À propos</h5>
                                                        <p className="text-muted">
                                                            Bienvenue dans votre espace client <strong>IPTrend</strong>.
                                                            Retrouvez ici toutes vos informations de compte, vos contrats de services
                                                            et l'historique de vos factures.
                                                        </p>
                                                        <Row>
                                                            <Col xs={6} md={4}>
                                                                <div className="d-flex mt-4">
                                                                    <div className="flex-shrink-0 avatar-xs align-self-center me-3">
                                                                        <div className="avatar-title bg-light rounded-circle fs-16 text-primary">
                                                                            <i className="ri-user-2-fill"></i>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex-grow-1 overflow-hidden">
                                                                        <p className="mb-1 text-muted">Identifiant :</p>
                                                                        <h6 className="text-truncate mb-0">{u.login || "—"}</h6>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            <Col xs={6} md={4}>
                                                                <div className="d-flex mt-4">
                                                                    <div className="flex-shrink-0 avatar-xs align-self-center me-3">
                                                                        <div className="avatar-title bg-light rounded-circle fs-16 text-primary">
                                                                            <i className="ri-mail-line"></i>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex-grow-1 overflow-hidden">
                                                                        <p className="mb-1 text-muted">Email :</p>
                                                                        <h6 className="text-truncate mb-0">{u.email || "—"}</h6>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            <Col xs={6} md={4}>
                                                                <div className="d-flex mt-4">
                                                                    <div className="flex-shrink-0 avatar-xs align-self-center me-3">
                                                                        <div className="avatar-title bg-light rounded-circle fs-16 text-primary">
                                                                            <i className="ri-building-line"></i>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex-grow-1 overflow-hidden">
                                                                        <p className="mb-1 text-muted">Entreprise :</p>
                                                                        <h6 className="text-truncate mb-0">{companyInfo?.name || "—"}</h6>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>

                                                {/* Coordonnées détaillées */}
                                                <Card>
                                                    <CardHeader className="align-items-center d-flex">
                                                        <h4 className="card-title mb-0 flex-grow-1">Coordonnées</h4>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <Row className="g-3">
                                                            {[
                                                                { label: "Prénom",          value: u.firstname, icon: "ri-user-line",        md: 6 },
                                                                { label: "Nom",             value: u.lastname,  icon: "ri-user-line",        md: 6 },
                                                                { label: "Email",           value: u.email,     icon: "ri-mail-line",        md: 12 },
                                                                { label: "Téléphone",       value: u.phone,     icon: "ri-phone-line",       md: 6 },
                                                                { label: "Mobile",          value: u.mobile,    icon: "ri-smartphone-line",  md: 6 },
                                                                { label: "Adresse",         value: u.address,   icon: "ri-map-pin-line",     md: 12 },
                                                            ].map((f) => (
                                                                <Col xs={12} md={f.md} key={f.label}>
                                                                    <div>
                                                                        <label className="form-label text-muted fw-medium fs-13">{f.label}</label>
                                                                        <div className="input-group">
                                                                            <span className="input-group-text">
                                                                                <i className={f.icon}></i>
                                                                            </span>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={f.value || "Non renseigné"}
                                                                                readOnly
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    </CardBody>
                                                </Card>

                                            </Col>
                                        </Row>
                                    </TabPane>

                                    {/* ── Tab 2 : Entreprise ── */}
                                    <TabPane tabId="2">
                                        <Row>
                                            <Col xxl={3}>
                                                <Card>
                                                    <CardBody>
                                                        <h5 className="card-title mb-3">Fiche entreprise</h5>
                                                        <div className="text-center mb-4">
                                                            <div className="avatar-lg mx-auto mb-3">
                                                                <span className="avatar-title rounded-circle fs-2 fw-bold text-white"
                                                                    style={{ background: "linear-gradient(135deg,#0066A1,#00A389)" }}>
                                                                    {companyInfo?.name?.[0]?.toUpperCase() || "E"}
                                                                </span>
                                                            </div>
                                                            <h5 className="mb-1">{companyInfo?.name || "—"}</h5>
                                                            <Badge color="success" className="fs-13 px-3 py-2">Client actif</Badge>
                                                        </div>
                                                        <div className="table-responsive">
                                                            <Table className="table-borderless mb-0">
                                                                <tbody>
                                                                    <tr>
                                                                        <th className="ps-0">SIRET :</th>
                                                                        <td className="text-muted">{companyInfo?.idprof2 || "—"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="ps-0">Code client :</th>
                                                                        <td className="text-muted">{companyInfo?.code_client || "—"}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="ps-0">Entité :</th>
                                                                        <td className="text-muted">{companyInfo?.entity || local?.entity || "—"}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>

                                            <Col xxl={9}>
                                                <Card>
                                                    <CardHeader className="align-items-center d-flex">
                                                        <h4 className="card-title mb-0 flex-grow-1">
                                                            <i className="ri-building-2-line me-2 text-primary"></i>
                                                            Informations entreprise
                                                        </h4>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <Row className="g-3">
                                                            {[
                                                                { label: "Raison sociale",  value: companyInfo?.name,          icon: "ri-building-2-line",  md: 12 },
                                                                { label: "SIRET",           value: companyInfo?.idprof2,        icon: "ri-file-copy-line",   md: 12 },
                                                                { label: "Email",           value: companyInfo?.email,          icon: "ri-mail-line",        md: 12 },
                                                                { label: "Téléphone",       value: companyInfo?.phone || companyInfo?.office_phone, icon: "ri-phone-line", md: 6 },
                                                                { label: "Mobile",          value: companyInfo?.phone_mobile,   icon: "ri-smartphone-line",  md: 6  },
                                                                { label: "Adresse",         value: companyInfo?.address,        icon: "ri-map-pin-line",     md: 8  },
                                                                { label: "Ville",           value: [companyInfo?.zip, companyInfo?.town].filter(Boolean).join(" "), icon: "ri-map-2-line", md: 4 },
                                                            ].map((f) => (
                                                                <Col xs={12} md={f.md} key={f.label}>
                                                                    <div>
                                                                        <label className="form-label text-muted fw-medium fs-13">{f.label}</label>
                                                                        <div className="input-group">
                                                                            <span className="input-group-text">
                                                                                <i className={f.icon}></i>
                                                                            </span>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={f.value || "Non renseigné"}
                                                                                readOnly
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    </CardBody>
                                                </Card>

                                                {/* Contacts */}
                                                <Card>
                                                    <CardHeader className="align-items-center d-flex">
                                                        <h4 className="card-title mb-0 flex-grow-1">
                                                            <i className="ri-contacts-line me-2 text-primary"></i>
                                                            Contact principal
                                                        </h4>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <div className="d-flex align-items-center">
                                                            <div className="flex-shrink-0 avatar-sm me-3">
                                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-18 fw-bold">
                                                                    {initials}
                                                                </span>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <h5 className="fs-15 mb-1">{fullName}</h5>
                                                                <p className="fs-13 text-muted mb-0">{u.email}</p>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                {u.phone && (
                                                                    <span className="badge bg-success-subtle text-success fs-12 px-3 py-2">
                                                                        <i className="ri-phone-line me-1"></i>{u.phone}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </TabPane>

                                    {/* ── Tab 3 : Utilisateurs ── */}
                                    <TabPane tabId="3">
                                        {addSuccess && (
                                            <Alert color="success" className="mb-3" toggle={() => setAddSuccess(null)}>
                                                <i className="ri-checkbox-circle-line me-2"></i>{addSuccess}
                                            </Alert>
                                        )}
                                        {deactError && (
                                            <Alert color="danger" className="mb-3" toggle={() => setDeactError(null)}>
                                                <i className="ri-error-warning-line me-2"></i>{deactError}
                                            </Alert>
                                        )}
                                        <Row>
                                            <Col lg={12}>
                                                <Card>
                                                    <CardHeader className="align-items-center d-flex">
                                                        <h4 className="card-title mb-0 flex-grow-1">
                                                            <i className="ri-team-line me-2 text-primary"></i>
                                                            Utilisateurs du compte
                                                        </h4>
                                                        <Badge color="primary" pill className="me-3">
                                                            {companyUsers?.users_count ?? 0}
                                                        </Badge>
                                                        <Button color="primary" size="sm" onClick={openAddModal}>
                                                            <i className="ri-user-add-line me-1"></i>
                                                            Ajouter un utilisateur
                                                        </Button>
                                                    </CardHeader>
                                                    <CardBody className="pt-0">
                                                        {!companyUsers ? (
                                                            <div className="text-center py-5">
                                                                <Spinner style={{ color: "#0066A1" }} />
                                                            </div>
                                                        ) : companyUsers.users.length === 0 ? (
                                                            <div className="text-center py-5 text-muted">
                                                                <i className="ri-user-line fs-2 mb-2 d-block"></i>
                                                                Aucun utilisateur trouvé.
                                                            </div>
                                                        ) : (
                                                            <div className="table-responsive">
                                                                <table className="table align-middle table-nowrap mb-0">
                                                                    <thead className="table-light text-muted">
                                                                        <tr>
                                                                            <th>Utilisateur</th>
                                                                            <th>Login</th>
                                                                            <th>Email</th>
                                                                            <th>Téléphone</th>
                                                                            <th>Mobile</th>
                                                                            <th>Poste</th>
                                                                            <th className="text-center">Statut</th>
                                                                            <th className="text-center">Actions</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(() => {
                                                                            // Calcul des contacts "signataire contrat" (type CUSTOMER ou libellé contient "signataire")
                                                                            const signataireIds = new Set<number>(
                                                                                (companyUsers.default_contacts || [])
                                                                                    .filter(dc =>
                                                                                        dc.type_code === "CUSTOMER" ||
                                                                                        (dc.type_label || "").toLowerCase().includes("signataire")
                                                                                    )
                                                                                    .map(dc => Number(dc.contact?.id || 0))
                                                                                    .filter(v => v > 0)
                                                                            );
                                                                            const myLogin = (local?.login || "").toLowerCase();
                                                                            return companyUsers.users.map((usr) => {
                                                                                const isActive = usr.is_active !== false && usr.statut !== 0;
                                                                                const isMe = !!myLogin && (usr.login || "").toLowerCase() === myLogin;
                                                                                const isSignataire = !!(usr as any).fk_socpeople && signataireIds.has((usr as any).fk_socpeople);
                                                                                const cannotDeactivate = isMe || isSignataire;
                                                                                const disabledReason = isMe
                                                                                    ? "Vous ne pouvez pas désactiver votre propre compte"
                                                                                    : isSignataire
                                                                                        ? "Contact signataire du contrat — non désactivable"
                                                                                        : "";
                                                                                return (
                                                                                <tr key={usr.id}>
                                                                                    <td>
                                                                                        <div className="d-flex align-items-center gap-2">
                                                                                            <div className="avatar-xs flex-shrink-0">
                                                                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-14 fw-semibold">
                                                                                                    {((usr.firstname?.[0] || "") + (usr.lastname?.[0] || "")).toUpperCase() || usr.login?.[0]?.toUpperCase() || "U"}
                                                                                                </span>
                                                                                            </div>
                                                                                            <span className="fw-medium">
                                                                                                {usr.fullname || [usr.firstname, usr.lastname].filter(Boolean).join(" ") || "—"}
                                                                                            </span>
                                                                                            {isMe && (
                                                                                                <Badge className="bg-info-subtle text-info ms-1 fs-10">Vous</Badge>
                                                                                            )}
                                                                                            {isSignataire && (
                                                                                                <Badge className="bg-warning-subtle text-warning ms-1 fs-10" title="Contact signataire contrat">
                                                                                                    <i className="ri-shield-star-line me-1"></i>Signataire
                                                                                                </Badge>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="text-muted fs-13">{usr.login || "—"}</td>
                                                                                    <td className="text-muted fs-13">{usr.email || "—"}</td>
                                                                                    <td className="text-muted fs-13">{usr.office_phone || "—"}</td>
                                                                                    <td className="text-muted fs-13">{usr.user_mobile || "—"}</td>
                                                                                    <td className="text-muted fs-13">{usr.job || "—"}</td>
                                                                                    <td className="text-center">
                                                                                        {isActive ? (
                                                                                            <Badge className="bg-success-subtle text-success text-uppercase">
                                                                                                <i className="ri-checkbox-circle-fill me-1"></i>Actif
                                                                                            </Badge>
                                                                                        ) : (
                                                                                            <Badge className="bg-danger-subtle text-danger text-uppercase">
                                                                                                <i className="ri-close-circle-fill me-1"></i>Inactif
                                                                                            </Badge>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        {isActive ? (
                                                                                            <Button
                                                                                                color="danger"
                                                                                                size="sm"
                                                                                                outline
                                                                                                disabled={cannotDeactivate || deactivatingId === usr.id}
                                                                                                title={disabledReason || "Désactiver l'utilisateur"}
                                                                                                onClick={() => deactivateUser(usr)}
                                                                                            >
                                                                                                {deactivatingId === usr.id
                                                                                                    ? <Spinner size="sm" />
                                                                                                    : <i className="ri-user-unfollow-line"></i>
                                                                                                }
                                                                                            </Button>
                                                                                        ) : (
                                                                                            <span className="text-muted fs-12">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                                );
                                                                            });
                                                                        })()}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </CardBody>
                                                </Card>

                                                {companyUsers && companyUsers.default_contacts_count > 0 && (
                                                    <Card>
                                                        <CardHeader className="align-items-center d-flex">
                                                            <h4 className="card-title mb-0 flex-grow-1">
                                                                <i className="ri-contacts-line me-2 text-primary"></i>
                                                                Contacts par défaut
                                                            </h4>
                                                        </CardHeader>
                                                        <CardBody>
                                                            <Row className="g-3">
                                                                {companyUsers.default_contacts.map((dc) => (
                                                                    <Col md={6} key={dc.assoc_id}>
                                                                        <div className="border rounded p-3">
                                                                            <p className="text-muted fs-12 mb-2 text-uppercase fw-semibold">
                                                                                {dc.type_label}
                                                                            </p>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <div className="avatar-xs flex-shrink-0">
                                                                                    <span className="avatar-title rounded-circle bg-info-subtle text-info fs-14">
                                                                                        <i className="ri-user-line"></i>
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    <p className="mb-0 fw-medium">{dc.contact.fullname || "—"}</p>
                                                                                    {dc.contact.poste && <p className="mb-0 text-muted fs-12">{dc.contact.poste}</p>}
                                                                                    {dc.contact.email && <p className="mb-0 text-muted fs-12">{dc.contact.email}</p>}
                                                                                    {dc.contact.phone && <p className="mb-0 text-muted fs-12">{dc.contact.phone}</p>}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                ))}
                                                            </Row>
                                                        </CardBody>
                                                    </Card>
                                                )}
                                            </Col>
                                        </Row>
                                    </TabPane>

                                </TabContent>
                            </div>
                        </Col>
                    </Row>

                </Container>
            </div>
            {/* ── Modal : Ajouter un utilisateur ── */}
            <Modal isOpen={modalAdd} toggle={() => setModalAdd(false)} centered>
                <ModalHeader toggle={() => setModalAdd(false)} className="border-bottom-dashed">
                    <i className="ri-user-add-line me-2 text-primary"></i>
                    Ajouter un utilisateur
                </ModalHeader>
                <ModalBody className="p-4">
                    {addError && (
                        <Alert color="danger" className="mb-3">
                            <i className="ri-error-warning-line me-2"></i>{addError}
                        </Alert>
                    )}
                    <Row className="g-3">
                        <Col xs={4}>
                            <label className="form-label text-muted fw-medium fs-13">Civilité</label>
                            <select
                                className="form-select"
                                value={addForm.civility}
                                onChange={e => setAddForm(f => ({ ...f, civility: e.target.value }))}
                            >
                                <option value="">—</option>
                                <option value="MR">M.</option>
                                <option value="MME">Mme</option>
                            </select>
                        </Col>
                        <Col xs={8}>
                            <label className="form-label text-muted fw-medium fs-13">Prénom</label>
                            <input
                                type="text" className="form-control"
                                placeholder="Prénom"
                                value={addForm.firstname}
                                onChange={e => setAddForm(f => ({ ...f, firstname: e.target.value }))}
                            />
                        </Col>
                        <Col xs={12}>
                            <label className="form-label text-muted fw-medium fs-13">Nom <span className="text-danger">*</span></label>
                            <input
                                type="text" className="form-control"
                                placeholder="Nom de famille"
                                value={addForm.lastname}
                                onChange={e => setAddForm(f => ({ ...f, lastname: e.target.value }))}
                            />
                        </Col>
                        <Col xs={12}>
                            <label className="form-label text-muted fw-medium fs-13">Poste / Fonction</label>
                            <input
                                type="text" className="form-control"
                                placeholder="Ex : Responsable IT"
                                value={addForm.poste}
                                onChange={e => setAddForm(f => ({ ...f, poste: e.target.value }))}
                            />
                        </Col>
                        <Col xs={12}>
                            <label className="form-label text-muted fw-medium fs-13">Email</label>
                            <input
                                type="email" className="form-control"
                                placeholder="email@entreprise.fr"
                                value={addForm.email}
                                onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                            />
                        </Col>
                        <Col xs={6}>
                            <label className="form-label text-muted fw-medium fs-13">Téléphone</label>
                            <input
                                type="text" className="form-control"
                                placeholder="01 23 45 67 89"
                                value={addForm.phone}
                                onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))}
                            />
                        </Col>
                        <Col xs={6}>
                            <label className="form-label text-muted fw-medium fs-13">Mobile</label>
                            <input
                                type="text" className="form-control"
                                placeholder="06 12 34 56 78"
                                value={addForm.phone_mobile}
                                onChange={e => setAddForm(f => ({ ...f, phone_mobile: e.target.value }))}
                            />
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter className="border-top-dashed">
                    <Button color="light" onClick={() => setModalAdd(false)} disabled={addLoading}>
                        Annuler
                    </Button>
                    <Button color="primary" onClick={submitAddContact} disabled={addLoading}>
                        {addLoading
                            ? <><Spinner size="sm" className="me-1" />Création…</>
                            : <><i className="ri-save-line me-1"></i>Enregistrer</>
                        }
                    </Button>
                </ModalFooter>
            </Modal>

        </React.Fragment>
    );
};

export default UserProfile;
